import { autoinject, observable, bindable, computedFrom } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { DialogService } from 'aurelia-dialog';
import { ConfirmDialog } from '_controls/confirm-dialog';
import { LocationDialog } from 'project-schedule/location-dialog';
import { LocationProductsDialog } from 'project-schedule/location-products-dialog';
import { HttpClient } from 'aurelia-http-client';
import * as Dto from '_models/dtos';
import { SessionData } from '_models/session-data';
import { UrlService } from '_services/url-service';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { SizeUtility } from '_controls/size-utility';
import { SortService } from '_services/sort-service';

@autoinject
export class ProjectSchedule {
    private _url = new UrlService();
    private _http: HttpClient;
    private _subscriptions: Subscription[] = [];
    private _lastSelectedLocationName: string;

    constructor(
        private _eventBus: EventAggregator,
        public router: Router,
        public sd: SessionData,
        public dialogSvc: DialogService
    ) {
        this._http = new HttpClient();
        this.specOutputUrl = this._url.build('~/api/SpecReport', true);
    }

    activeTabIndex = 0;
    
    specOutputUrl: string;
    
    productSorter = new SortService('colorNumber', 'asc');

    sortProducts(propertyName: string) {
        this.productSorter.sort(this.sd.allSelectedProducts, propertyName);
    }

    /**
      * Projects all selected products-colors and their assigned locations in the 'products' tab.
      */
    @computedFrom('sd.allSelectedProducts.length', 'activeTabIndex') // Refreshes 'products' when array length changes, or when tab index changes
    get products() : Dto.ScheduledProductProjection[] {
        // Display all product-color selections and also include any assigned locations
        var productsWithRooms = this.sd.allSelectedProducts.map(p => {
            // Get scheduled locations for each product-color selection
            var scheduledLocations = this.sd.schedule.filter(s => s.color.colorUID== p.color.colorUID).map(s => s.location);            
            return new Dto.ScheduledProductProjection(p.productUse, p.substrate, p.product, p.color, scheduledLocations);
        });
        return productsWithRooms;
    }

    /**
      * Projects all created locations and their assigned products-colors in the 'locations' tab.
      */
    @computedFrom('sd.locations.length', 'activeTabIndex') // Refreshes 'locations' when array length changes, or when tab index changes
    get locations(): Dto.ScheduledLocationProjection[] {
        // Display all locations along with a list of assigned product-color selections
        var locationsWithProducts = this.sd.locations.map(locName => {
            // Get scheduled product-color selections for each location
            var scheduledProducts = this.sd.schedule.filter(s => s.location == locName);
            return new Dto.ScheduledLocationProjection(locName, scheduledProducts);
        }).sort((a, b) => {
            if (a.name > b.name) return 1;
            else if (b.name > a.name) return -1;
            else return 0;
        });

        // Try to open the selected location
        var selectedLocation = locationsWithProducts.find(l => l.name == this._lastSelectedLocationName);
        if (selectedLocation)
            selectedLocation.isOpen = true;

        return locationsWithProducts;
    }

    /**
     * Toggles a ScheduledLocationProjection.
     */
    toggle(loc: Dto.ScheduledLocationProjection) {
        loc.isOpen = !loc.isOpen;

        if (loc.isOpen)
            this._lastSelectedLocationName = loc.name;
        else
            this._lastSelectedLocationName = null;
    }

    /**
     * Removes a product from the 'products' tab along with any room assignments.
     * @param sel - The selected product.
     */
    removeProduct(sel: Dto.ScheduledProductProjection) {

        // Confirm
        this.dialogSvc.open({
            viewModel: ConfirmDialog,
            model: `Are you sure you want to remove '${sel.product.productFamily} - ${sel.color.colorName}'?`
        }).whenClosed(e => {

            if (!e.wasCancelled) {

                // Remove product from allSelectedProducts (should be exactly one entry)
                var idx = this.sd.allSelectedProducts.findIndex(item => item.product.productFamily == sel.product.productFamily && item.color.colorUID == sel.color.colorUID);
                this.sd.allSelectedProducts.splice(idx, 1);

                // Remove product from schedule (may be multiple entries)
                this.sd.schedule = this.sd.schedule.filter(item => !(item.product.productFamily == sel.product.productFamily && item.color.colorUID == sel.color.colorUID));

                this.sd.save();
            }
        });
    }

    addLocation() {

        var defaultLocationName = '';

        // Show 'Location' dialog
        this.dialogSvc.open({
            viewModel: LocationDialog,
            model: defaultLocationName
        }).whenClosed(e => {

            if (!e.wasCancelled) {

                var locationName = e.output;
                this._lastSelectedLocationName = locationName;
                this.sd.locations.push(locationName);
                this.sd.save();
            }
        });
    }

    editLocation(location: Dto.ScheduledLocationProjection, evt: Event) {

        var oldLocationName = location.name;

        // Show 'Location' dialog
        this.dialogSvc.open({
            viewModel: LocationDialog,
            model: location.name
        }).whenClosed(e => {

            if (!e.wasCancelled) {

                var newLocationName = e.output;

                // Update session locations
                var idx = this.sd.locations.findIndex(l => l == oldLocationName);
                this.sd.locations[idx] = newLocationName;

                // Update session schedule
                this.sd.schedule
                    .filter(s => s.location == oldLocationName)
                    .forEach(s => s.location = newLocationName);

                // Update view manually (rather than refreshing entire 'locations' projection)
                location.name = newLocationName;

                this.sd.save();
            }
        });

        evt.stopPropagation();
    }

    removeLocation(location: Dto.ScheduledLocationProjection, evt: Event) {
        // Confirm
        this.dialogSvc.open({
            viewModel: ConfirmDialog,
            model: `Are you sure you want to remove '${location.name}'?`
        }).whenClosed(e => {

            if (!e.wasCancelled) {

                // Update schedule in session
                this.sd.schedule
                    .filter(s => s.location == location.name)
                    .forEach(s => {
                        var idx = this.sd.schedule.findIndex(s => s.location == location.name);
                        this.sd.schedule.splice(idx, 1)
                    });

                var idx = this.sd.locations.findIndex(l => l == location.name);
                this.sd.locations.splice(idx, 1);

                this.sd.save();
            }
        });

        evt.stopPropagation();
    }

    addProductsToLocation(location: Dto.ScheduledLocationProjection) {
        // Show 'Add Product to Location' dialog
        this.dialogSvc.open({
            viewModel: LocationProductsDialog,
            model: location
        }).whenClosed(e => {

            if (!e.wasCancelled) {

                var selectedProducts : Dto.WizardProductColorSelection[] = e.output;

                // Get schedule items that haven't already been added
                var nonExistingProducts = selectedProducts.filter(p => {
                    var existsInSchedule = this.sd.schedule.some(s =>
                        s.productUse.id == p.productUse.id && 
                        s.substrate.id == p.substrate.id &&
                        s.location == location.name &&
                        s.product.productFamily == p.product.productFamily &&
                        s.sheenName == p.sheenName &&
                        s.color.colorUID == p.color.colorUID);
                    return !existsInSchedule;
                });

                // Update schedule in session
                var scheduleItems = nonExistingProducts
                    .map(p => new Dto.ScheduleItem(p.productUse, p.substrate, p.product, p.color, location.name));

                scheduleItems.forEach(s => this.sd.schedule.push(s));

                // Update location view manually (rather than refreshing entire 'locations' projection)
                scheduleItems.forEach(p => location.selections.push(p));

                // Re-apply current sort for this location's product selections
                location.sorter.sort(location.selections, location.sorter.sortProperty, location.sorter.sortDirection);

                this.sd.save();
            }
        });
    }
    
    removeProductFromLocation(selection: Dto.WizardProductColorSelection, location: Dto.ScheduledLocationProjection) {

        // Confirm
        this.dialogSvc.open({
            viewModel: ConfirmDialog,
            model: `Are you sure you want to remove '${selection.product.productFamily} - ${selection.color.colorName}' from '${location.name}'?`
        }).whenClosed(e => {

            if (!e.wasCancelled) {

                // Update schedule in session
                var idx = this.sd.schedule.findIndex(s => s.location == location.name && s.product.productFamily == selection.product.productFamily && s.color.colorUID == selection.color.colorUID);
                this.sd.schedule.splice(idx, 1);

                // Update location view manually (rather than refreshing entire 'locations' projection)
                var idx2 = location.selections.findIndex(s => s.product.productFamily == selection.product.productFamily && s.color.colorUID == selection.color.colorUID);
                location.selections.splice(idx2, 1); 

                this.sd.save();
            }
        });
    }

    goToProductSelector() {
        var url = this._url.build('~/product-selector');
        this.router.navigate(url);
    }

    prepareForm(ev: Event) {
        
        // Get scheduled items for report
        var scheduledItems = this.sd.schedule.map(item => {
            var dto = new Dto.ProjectScheduleItem();
            
            dto.LocationName = `${item.location} - ${item.productUse.application} - ${item.substrate.longName}`;
            dto.LocationTag = item.notes;
            dto.Color = item.color.color;
            dto.ColorName = item.color.colorName;
            dto.ColorNumber = item.color.colorNumber;
            dto.ColorUID = item.color.colorUID;            
            dto.ProductFamily = item.productFamily;
            dto.ProductName = item.product.productFamily;
            dto.ProductNumber = item.product.productNumber;
            dto.SheenId = 0; // TODO: Implement SheenId
            dto.SheenName = item.product.sheenName;
            return dto;
        });

        // Also include products that have not been added to the schedule (no location)
        var nonScheduledItems = this.sd.allSelectedProducts.filter(p => {
            var productInSchedule = this.sd.schedule.find(s => s.product.productNumber == p.product.productNumber);
            return productInSchedule == null;
        }).map(item => {
            var dto = new Dto.ProjectScheduleItem();

            dto.LocationName = 'NO LOCATION ASSIGNED'; // Assigned via the ROOM TAGS tab
            dto.LocationTag = ''; // Assigned via the ROOM TAGS tab
            dto.Color = item.color.color;
            dto.ColorName = item.color.colorName;
            dto.ColorNumber = item.color.colorNumber;
            dto.ColorUID = item.color.colorUID;            
            dto.ProductFamily = item.productFamily;
            dto.ProductName = item.product.productFamily;
            dto.ProductNumber = item.product.productNumber;
            dto.SheenId = 0; // TODO: Implement SheenId
            dto.SheenName = item.product.sheenName;
            return dto;
            });

        var allItems = scheduledItems.concat(nonScheduledItems);

        var form = <HTMLFormElement>ev.srcElement.parentElement;
        form.elements['scheduleItems'].value = JSON.stringify(allItems);
        form.submit();
    }
}
